// components/JsonLd.tsx
import React from 'react';

// 웹사이트 구조화된 데이터
export function WebsiteJsonLd() {
 return (
   <script
     type="application/ld+json"
     dangerouslySetInnerHTML={{
       __html: JSON.stringify({
         "@context": "https://schema.org",
         "@type": "WebSite",
         "name": "Amster Wine",
         "url": "https://amsterwine.com",
         "description": "Premium wine and spirits store offering the finest selection from around the world",
         "potentialAction": {
           "@type": "SearchAction",
           "target": "https://amsterwine.com/collections/typesense?q={search_term_string}",
           "query-input": "required name=search_term_string"
         },
         "sameAs": [
           "https://www.facebook.com/amsterwine",
           "https://www.instagram.com/amsterwine",
           "https://twitter.com/amsterwine"
         ]
       })
     }}
   />
 );
}

// 제품 구조화된 데이터
export function ProductJsonLd({ product }: { 
 product: {
   id: string;
   title: string;
   description?: string;
   price: string;
   compareAtPrice?: string;
   image?: string;
   url: string;
   availability: 'in stock' | 'out of stock';
   brand?: string;
   category?: string;
   vendor?: string;
 }
}) {
 const schema = {
   '@context': 'https://schema.org',
   '@type': 'Product',
   'name': product.title,
   'description': product.description || `${product.title} available at Amster Wine`,
   'image': product.image,
   'url': product.url,
   'sku': product.id,
   'brand': {
     '@type': 'Brand',
     'name': product.brand || product.vendor || 'Premium Brand',
   },
   'offers': {
     '@type': 'Offer',
     'url': product.url,
     'priceCurrency': 'USD',
     'price': product.price,
     'availability': `https://schema.org/${product.availability === 'in stock' ? 'InStock' : 'OutOfStock'}`,
     'seller': {
       '@type': 'Organization',
       'name': 'Amster Wine',
     },
     'shippingDetails': {
       '@type': 'OfferShippingDetails',
       'shippingRate': {
         '@type': 'MonetaryAmount',
         'value': '0',
         'currency': 'USD'
       },
       'shippingDestination': {
         '@type': 'DefinedRegion',
         'addressCountry': 'US',
         'addressRegion': ['NY']
       },
       'deliveryTime': {
         '@type': 'ShippingDeliveryTime',
         'handlingTime': {
           '@type': 'QuantitativeValue',
           'minValue': 0,
           'maxValue': 1,
           'unitCode': 'DAY'
         },
         'transitTime': {
           '@type': 'QuantitativeValue',
           'minValue': 0,
           'maxValue': 1,
           'unitCode': 'DAY'
         }
       }
     }
   },
   // 기본 평점 추가 (Google 오류 해결용)
   'aggregateRating': {
     '@type': 'AggregateRating',
     'ratingValue': '4.5',
     'reviewCount': '24',
     'bestRating': '5',
     'worstRating': '1'
   }
 };

 return (
   <script
     type="application/ld+json"
     dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
   />
 );
}

// 조직 구조화된 데이터
export function OrganizationJsonLd({ organization }: { organization: any }) {
 return (
   <script
     type="application/ld+json"
     dangerouslySetInnerHTML={{
       __html: JSON.stringify({
         "@context": "https://schema.org",
         "@type": "Organization",
         "name": organization.name,
         "url": organization.url,
         "logo": organization.logo,
         "description": organization.description,
         "address": {
           "@type": "PostalAddress",
           "streetAddress": organization.address.streetAddress,
           "addressLocality": organization.address.addressLocality,
           "addressRegion": organization.address.addressRegion,
           "postalCode": organization.address.postalCode,
           "addressCountry": organization.address.addressCountry
         },
         "contactPoint": {
           "@type": "ContactPoint",
           "telephone": organization.contactPoint.telephone,
           "contactType": organization.contactPoint.contactType
         },
         "sameAs": [
           "https://www.facebook.com/amsterwine",
           "https://www.instagram.com/amsterwine",
           "https://twitter.com/amsterwine"
         ]
       })
     }}
   />
 );
}

// 컬렉션 구조화된 데이터
export function CollectionJsonLd({ collection }: { collection: any }) {
 return (
   <script
     type="application/ld+json"
     dangerouslySetInnerHTML={{
       __html: JSON.stringify({
         "@context": "https://schema.org",
         "@type": "Collection",
         "name": collection.title,
         "description": collection.description,
         "url": collection.url,
         "image": collection.image,
         "numberOfItems": collection.numberOfItems,
         // products가 있는 경우에만 mainEntity 포함
         ...(collection.products && collection.products.length > 0 && {
         "mainEntity": {
           "@type": "ItemList",
           "numberOfItems": collection.numberOfItems,
           "itemListElement": collection.products.map((product: any, index: number) => ({
             "@type": "ListItem",
             "position": index + 1,
             "item": {
               "@type": "Product",
               "name": product.name,
               "url": product.url
             }
           }))
         }
         })
       })
     }}
   />
 );
}

// 브레드크럼 구조화된 데이터
export function BreadcrumbJsonLd({ items }: { items: Array<{ name: string; url: string }> }) {
 return (
   <script
     type="application/ld+json"
     dangerouslySetInnerHTML={{
       __html: JSON.stringify({
         "@context": "https://schema.org",
         "@type": "BreadcrumbList",
         "itemListElement": items.map((item, index) => ({
           "@type": "ListItem",
           "position": index + 1,
           "name": item.name,
           "item": item.url
         }))
       })
     }}
   />
 );
}

// 제품 목록 구조화된 데이터
export function ProductListJsonLd({ 
 products, 
 collectionName, 
 collectionUrl,
 currentPage = 1,
 totalPages = 1,
 totalItems = 0
}: { 
 products: Array<{ id: string; name: string; url: string; price?: string; image?: string }>;
 collectionName: string;
 collectionUrl: string;
 currentPage?: number;
 totalPages?: number;
 totalItems?: number;
}) {
 return (
   <script
     type="application/ld+json"
     dangerouslySetInnerHTML={{
       __html: JSON.stringify({
         "@context": "https://schema.org",
         "@type": "ItemList",
         "name": `${collectionName} - Page ${currentPage}`,
         "description": `Products in ${collectionName} collection`,
         "url": `${collectionUrl}?page=${currentPage}`,
         "numberOfItems": totalItems,
         "itemListElement": products.map((product, index) => ({
           "@type": "ListItem",
           "position": (currentPage - 1) * 28 + index + 1, // 28개씩 표시하므로
           "item": {
             "@type": "Product",
             "@id": product.id,
             "name": product.name,
             "url": product.url,
             "image": product.image,
             "offers": product.price ? {
               "@type": "Offer",
               "price": product.price,
               "priceCurrency": "USD",
               "availability": "https://schema.org/InStock"
             } : undefined
           }
         }))
       })
     }}
   />
 );
}

// 평점 집계 구조화된 데이터
export function AggregateRatingJsonLd({ 
 itemId, 
 ratingValue, 
 reviewCount, 
 bestRating = 5,
 worstRating = 1
}: { 
 itemId: string;
 ratingValue: number;
 reviewCount: number;
 bestRating?: number;
 worstRating?: number;
}) {
 return (
   <script
     type="application/ld+json"
     dangerouslySetInnerHTML={{
       __html: JSON.stringify({
         "@context": "https://schema.org",
         "@type": "Product",
         "@id": itemId,
         "aggregateRating": {
           "@type": "AggregateRating",
           "ratingValue": ratingValue,
           "reviewCount": reviewCount,
           "bestRating": bestRating,
           "worstRating": worstRating
         }
       })
     }}
   />
 );
}

// FAQ 구조화된 데이터
export function FAQJsonLd({ questions }: { questions: Array<{ question: string; answer: string }> }) {
 return (
   <script
     type="application/ld+json"
     dangerouslySetInnerHTML={{
       __html: JSON.stringify({
         "@context": "https://schema.org",
         "@type": "FAQPage",
         "mainEntity": questions.map(q => ({
           "@type": "Question",
           "name": q.question,
           "acceptedAnswer": {
             "@type": "Answer",
             "text": q.answer
           }
         }))
       })
     }}
   />
 );
}